import "./App.css";
import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { Activity, Target, FileText, MessageSquare, Globe, Shield, Bell, BarChart } from 'lucide-react';
import { translations } from './translations';
import Home from './pages/Home';
import ROICalculator from './pages/ROICalculator';
import ComingSoon from './pages/ComingSoon';
import Footer from './components/Footer';
import DriviLogo from "./assets/logo.svg";
import { Helmet } from 'react-helmet-async';

function App() {
  const [language, setLanguage] = useState("es");
  const t = translations[language];
  const location = useLocation();
  const navigate = useNavigate();

  // Detect language from URL or browser
  useEffect(() => {
    const pathLang = location.pathname.split('/')[1];
    if (pathLang === 'en' || pathLang === 'es') {
      setLanguage(pathLang);
    } else {
      // Default to Spanish if no language in URL
      setLanguage("es");
      // Redirect to language-specific URL
      navigate(`/es${location.pathname}`, { replace: true });
    }
  }, [location.pathname, navigate]);

  // Set HTML lang attribute
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const handleLanguageChange = (newLang) => {
    setLanguage(newLang);
    const currentPath = location.pathname;
    const newPath = currentPath.startsWith('/es') 
      ? currentPath.replace('/es', `/${newLang}`)
      : currentPath.startsWith('/en')
        ? currentPath.replace('/en', `/${newLang}`)
        : `/${newLang}${currentPath}`;
    navigate(newPath);
  };

  const scrollToSection = (sectionId) => {
    // If we're not on the home page, first navigate to home
    if (location.pathname !== `/${language}`) {
      navigate(`/${language}`, { state: { scrollTo: sectionId } });
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80; // Height of the fixed header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Google Analytics solo en producción
  React.useEffect(() => {
    const isLocal = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
    if (!isLocal) {
      // Google Analytics
      const script = document.createElement("script");
      script.async = true;
      script.src = "https://www.googletagmanager.com/gtag/js?id=G-BJKV3P7RQ6";
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      function gtag(){window.dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'G-BJKV3P7RQ6');
    }
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      {/* SEO Meta Tags */}
      <Helmet>
        <html lang={language} />
        <title>{t.seo.title}</title>
        <meta name="description" content={t.seo.description} />
        <meta name="keywords" content={t.seo.keywords} />
        <meta name="author" content="Drivi" />
        <meta name="robots" content="index, follow" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <meta name="theme-color" content="#3498DB" />
        
        {/* Open Graph */}
        <meta property="og:type" content={t.seo.og.type} />
        <meta property="og:title" content={t.seo.og.title} />
        <meta property="og:description" content={t.seo.og.description} />
        <meta property="og:image" content={t.seo.og.image} />
        <meta property="og:url" content={`https://www.drivi.io/${language}${location.pathname.replace(/^\/[a-z]{2}/, '')}`} />
        <meta property="og:locale" content={language === 'es' ? 'es_ES' : 'en_US'} />
        <meta property="og:locale:alternate" content={language === 'es' ? 'en_US' : 'es_ES'} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content={t.seo.twitter.card} />
        <meta name="twitter:title" content={t.seo.twitter.title} />
        <meta name="twitter:description" content={t.seo.twitter.description} />
        <meta name="twitter:image" content={t.seo.twitter.image} />
        <meta name="twitter:site" content={t.seo.twitter.site} />
        
        {/* Canonical y alternates multilenguaje */}
        <link rel="canonical" href={`https://www.drivi.io/${language}${location.pathname.replace(/^\/[a-z]{2}/, '')}`} />
        <link rel="alternate" hreflang="es" href={`https://www.drivi.io/es${location.pathname.replace(/^\/[a-z]{2}/, '')}`} />
        <link rel="alternate" hreflang="en" href={`https://www.drivi.io/en${location.pathname.replace(/^\/[a-z]{2}/, '')}`} />
        <link rel="alternate" hreflang="x-default" href="https://www.drivi.io/es" />
      </Helmet>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to={`/${language}`} className="flex items-center header-logo">
              <img src={DriviLogo} alt="Drivi" className="h-8 w-auto" />
              <span className="ml-2 text-xl font-bold text-[#3498DB]">Drivi</span>
            </Link>
            <div className="flex items-center space-x-8">
              <nav className="hidden md:flex space-x-8">
                <button
                  onClick={() => scrollToSection('features')}
                  className="header-nav-link text-gray-600 flex items-center space-x-2"
                >
                  <Activity className="h-5 w-5" />
                  <span>{t.nav.features}</span>
                </button>
                <button
                  onClick={() => scrollToSection('trust')}
                  className="header-nav-link text-gray-600 flex items-center space-x-2"
                >
                  <Shield className="h-5 w-5" />
                  <span>{t.nav.trust}</span>
                </button>
                <button
                  onClick={() => scrollToSection('waitlist')}
                  className="header-nav-link text-gray-600 flex items-center space-x-2"
                >
                  <Bell className="h-5 w-5" />
                  <span>{t.nav.waitlist}</span>
                </button>
                <button
                  onClick={() => scrollToSection('pricing')}
                  className="header-nav-link text-gray-600 flex items-center space-x-2"
                >
                  <FileText className="h-5 w-5" />
                  <span>{t.nav.pricing}</span>
                </button>
                <button
                  onClick={() => scrollToSection('case-studies')}
                  className="header-nav-link text-gray-600 flex items-center space-x-2"
                >
                  <BarChart className="h-5 w-5" />
                  <span>{t.nav.caseStudies}</span>
                </button>
                <button
                  onClick={() => scrollToSection('support')}
                  className="header-nav-link text-gray-600 flex items-center space-x-2"
                >
                  <MessageSquare className="h-5 w-5" />
                  <span>{t.nav.support}</span>
                </button>
              </nav>
              <button
                onClick={() => handleLanguageChange(language === "en" ? "es" : "en")}
                className="header-button flex items-center space-x-2 px-4 py-2 rounded-lg text-gray-600 border border-gray-200"
              >
                <Globe className="h-5 w-5" />
                <span className="font-medium">{language === "en" ? "EN" : "ES"}</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow">
        <Routes>
          <Route path="/es" element={<Home language="es" />} />
          <Route path="/en" element={<Home language="en" />} />
          <Route path="/es/roi-calculator" element={<ROICalculator language="es" />} />
          <Route path="/en/roi-calculator" element={<ROICalculator language="en" />} />
          <Route path="/es/coming-soon" element={<ComingSoon language="es" />} />
          <Route path="/en/coming-soon" element={<ComingSoon language="en" />} />
          <Route path="*" element={<Home language="es" />} />
        </Routes>
      </main>

      {/* Footer */}
      <Footer language={language} />
    </div>
  );
}

export default App;
